<template>
  <el-main v-loading="client.loading" class="send-docutax-request">
    <div class="upload-section">
      <h1>Submit Tax Documents</h1>
      <p>Submit your tax documents (i.e., W-2's, 1099's & photo ID), conveniently and securely by simply taking a
        picture of each document using your smart phone or tablet.</p>
      <el-upload :disabled="!uploadConfiguration.isInitialized" class="uploader" drag :action="uploadConfiguration.action"
        :accept="uploadConfiguration.accept" :data="uploadConfiguration.data" :before-upload="onBeforeUpload"
        :on-error="onUploadError" ref="uploader" :http-request="handleUploadRequest" multiple>
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          Drop file here or <em>click</em> to take a picture or upload a file<br />
          <small>jpg/png/pdf files with a size less than 50mb</small>
        </div>
      </el-upload>
      <el-form class="mt-3" label-position="top" @submit.native.prevent>
        <el-form-item label="Message (optional)">
          <el-input type="textarea" maxlength="500" :autosize="{ minRows: 3, maxRows: 6 }"
            placeholder="Include a message to your tax preparer" v-model="documentSubmission.message" />
        </el-form-item>
        <el-form-item v-loading="client.selectedOffice.loading" label="Selected Office">
          <SelectOfficeCard v-if="client.selectedOffice.data" :office="client.selectedOffice.data"
            @unselect="onRequestUnSelectOffice" selected />
          <i v-else>No office selected</i>
        </el-form-item>
      </el-form>
      <el-row class="mt-3" type="flex" align="middle" justify="center">
        <el-button class="mt-3" v-loading="submitting" type="primary" @click="onSubmit" round
          style="min-width: 200px;">Submit</el-button>
      </el-row>
    </div>
    <select-office-dialog :visible="!client.loading && !hasSelectedOffice" :has-selected-office="hasSelectedOffice"
      @client-updated="onClientUpdated" />
    <el-dialog :visible="submissionIsComplete" :show-close="false">
      <el-result icon="success" title="Success!"
        subTitle="One of our virtual tax pros will review your documents and prepare your tax return. Upon completion, the tax professional will contact you with any additional questions to finalize your tax return." />
    </el-dialog>
  </el-main>
</template>

<script>
import api from '@/api';
import jwtService from '@/service/JwtService';
import Client from '@/model/Client';
import Office from '@/model/Office';
import SelectOfficeCard from '@/component/SelectOfficeCard.vue';
import SelectOfficeDialog from '@/component/SelectOfficeDialog.vue';
import elementDefaultUploadHandler from 'element-ui/packages/upload/src/ajax';

export default {
  Office,
  components: {
    SelectOfficeCard,
    SelectOfficeDialog,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  created() {
    if (!this.validateAccessToken()) {
      this.$router.push({ name: 'home' });
      jwtService.reset();
      return;
    }

    this.loadClient();
  },
  data() {
    return {
      client: {
        loading: true,
        data: undefined,
        selectedOffice: {
          loading: false,
          data: undefined,
        },
      },
      suggestedOffice: {
        loading: false,
        data: undefined,
      },
      submitting: false,
      submissionIsComplete: false,
      documentSubmission: {
        message: '',
        files: [],
      },
      uploadConfiguration: {
        isInitialized: false,
        action: '',
        data: undefined,
        accept: 'image/jpeg,image/png,application/pdf,image/heic,image/heic-sequence',
      },
      renamedFileTracker: {},
    };
  },
  computed: {
    hasSelectedOffice() {
      return !!this.client.data && !!this.client.data.selectedOfficeId;
    },
  },
  watch: {
    'client.data.selectedOfficeId': {
      handler(officeId) {
        if (officeId === Office.VIRTUAL_OFFICE.id) {
          this.client.selectedOffice.data = Office.VIRTUAL_OFFICE;
          return;
        }

        if (!officeId) {
          this.client.selectedOffice.data = undefined;
          return;
        }

        this.loadUserSelectedOffice(officeId);
      },
    },
  },
  methods: {
    validateAccessToken() {
      const jwt = jwtService.get();

      if (!jwt || jwt.userId !== this.clientId) {
        this.$message.error('Your access token is not valid');
        return false;
      } if (jwt.isExpired()) {
        this.$message.error('Your session has expired');
        return false;
      }

      return true;
    },
    async loadClient() {
      try {
        this.client.loading = true;
        const response = await api.docutax.fetchClient();
        this.client.data = new Client(response.data);

        if (this.client.data.selectedOfficeId === undefined) {
          this.loadSuggestedOffice();
        }

        const uploadUrlResponse = await api.docutax.getUploadUrl();
        this.configureUpload(uploadUrlResponse.data);
      } catch (e) {
        console.error('this is bad - client would not load...', e);
      } finally {
        this.client.loading = false;
      }
    },
    async loadSuggestedOffice() {
      try {
        this.suggestedOffice.loading = true;
        const response = await api.docutax.findLastFiledOffice();
        if (response.data) {
          this.suggestedOffice.data = new Office(response.data);
          this.locationSearch.selectedOffice = this.suggestedOffice.data;
        }
      } catch (e) {
        console.warn('failed to load suggested office', e);
      } finally {
        this.suggestedOffice.loading = false;
      }
    },
    async loadUserSelectedOffice(officeId) {
      try {
        this.client.selectedOffice.loading = true;
        const response = await api.docutax.fetchLocationById(officeId);
        if (response.data) {
          this.client.selectedOffice.data = new Office(response.data);
        }
      } catch (e) {
        console.warn('failed to load selected office', e);
      } finally {
        this.client.selectedOffice.loading = false;
      }
    },
    onClientUpdated(client) {
      this.client.data = client;
    },
    onRequestUnSelectOffice() {
      this.client.data.selectedOfficeId = undefined;
    },
    configureUpload(uploadDetails) {
      this.uploadConfiguration.isInitialized = true;
      this.uploadConfiguration.action = uploadDetails.attributes.action;
      this.uploadConfiguration.data = uploadDetails.inputs;
    },
    handleUploadRequest(options) {
      // generates unique filename for each uploaded file
      const [originalFileName, extension] = options.file.name.split(/\.(?=[^.]+$)/);
      const uid = Math.random().toString(36).slice(-6);
      const uniqueFileName = `${originalFileName}-${uid}.${extension}`;
      this.renamedFileTracker[options.file.uid] = uniqueFileName;

      options.file = new File([options.file], uniqueFileName, {
        ...options.file,
      });
      elementDefaultUploadHandler(options);
    },
    onBeforeUpload(file) {
      if (file.size >= 52428800) {
        this.$message.error(`${file.name} is too large, file size must be less than 50mb.`);
        return false;
      }

      return true;
    },
    onUploadError(error, file) {
      console.error('upload failed', file, error);
      this.$message.error(`Failed to upload file named ${file.name}`);
    },
    async onSubmit() {
      const files = this.$refs.uploader.uploadFiles;
      if (files.length === 0) {
        if (this.documentSubmission.message.trim().length === 0) {
          this.$message.error('Please upload a file or include a message to your tax preparer.');
          return;
        }

        try {
          await this.$confirm('No files have been included. Only your message will be sent to your preparer. Continue?');
        } catch (e) {
          // user hit cancel
          return;
        }
      }

      const incompleteFiles = files.filter(file => file.status !== 'success');
      if (incompleteFiles.length) {
        try {
          console.warn('these are still uploading', incompleteFiles);
          await this.$confirm('Some files have not finished uploading. Are you sure you want to continue?');
        } catch (e) {
          // user hit cancel
          return;
        }
      }

      // lookup unique filename generated in handleUploadRequest method
      this.documentSubmission.files = files.map(file => this.renamedFileTracker[file.uid])
        .filter(fileName => fileName != null);

      try {
        this.submitting = true;
        await api.docutax.submit(this.documentSubmission);
        this.submissionIsComplete = true;
      } catch (e) {
        console.error('Submission failed', e);
        this.$message.error('Failed to submit your documents, please try again');
      } finally {
        this.submitting = false;
      }
    },
    returnToHome() {
      this.$router.push({ name: 'home' });
    },
  },
};
</script>

<style lang="scss">
.el-main.send-docutax-request {
  display: block;
  padding: 18px 18px 100px 18px;

  .upload-section {
    max-width: 700px;
    margin: 0 auto;

    .uploader .el-upload {
      width: 100%;

      .el-upload-dragger {
        width: 100%;
        max-width: 100%;
      }
    }
  }
}

.el-dialog.select-office-dialog {
  width: 80%;
  max-width: 900px;

  .zipcode-search {
    .zipcode-form-item {
      max-width: 200px;

      .input-button {
        background-color: #293896 !important;
        color: #fff !important;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        font-weight: bold;
      }
    }
  }

  .search-empty-state {
    font-style: italic;
    color: #999;
    padding: 24px;
    text-align: center;
    border: 1px dotted #ccc;

    &.no-results-found {
      font-style: normal;
      color: #333;
    }
  }
}
</style>
